import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ClientUser, UserInvite } from '../../../models/client-user-management';
import { InstanceTypes } from '../../../models/instance-types';
import { AppConfigService } from '../../../services/app-config.service';

import {
  PermissionGroup,
  PermissionOption,
  SelectablePermission
} from '../permissions-list/permissions-list.component';
import { EmailDomainBlacklistValidator } from '../../../utils/emailDomainBlacklistValidator';
import { forkJoin, Observable, of } from 'rxjs';
import { ClientFeatureWhitelistService } from '../../../services/client-feature-whitelist.service';
import { Select, Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';
import { mergeMap } from 'rxjs/operators';
import { SwipeclockIdValidator } from '../../../utils/SwipeclockIdValidator';
import { CompaniesService } from '../../../services/companies.service';

@Component({
  selector: 'app-client-user-invite-modal',
  templateUrl: './client-user-invite-modal.component.html',
  styleUrls: ['./client-user-invite-modal.component.css'],
  standalone: false
})
export class ClientUserInviteModalComponent implements OnInit {
  @Output()
  public onCloseDetails: EventEmitter<any> = new EventEmitter();
  @ViewChild('inviteModal', { static: true })
  public modalContent: TemplateRef<any>;

  @Input()
  public initialModel: ClientUser | null;
  @Select(ClientSelectionState.hasTimeclockSetup)
  public hasTimeclock: Observable<boolean>;

  public companySwipeclockIdSynched = false;

  private modalRef: NgbModalRef;
  @Input()
  public clientCode: string;

  public invalidDomain: string | null = null;
  public invalidTimekeepingId: boolean = false;
  get buttonText() {
    return this.initialModel == null ? 'Send Invite' : 'Save';
  }
  public get isClientPortal() {
    return this.env.instanceType === InstanceTypes.ClientPortal;
  }

  public get isCsrPortal() {
    return this.env.instanceType === InstanceTypes.CsrPortal;
  }
  public get availablePermissions(): string[] {
    return this.appConfigService.getConfig().UserManagement.AvailablePermissions;
  }
  public test = true;
  public form: UntypedFormGroup;

  public permissions: PermissionGroup[] = [
    new PermissionGroup('People', [new PermissionOption('EditPeople', 'Add / Edit / Delete')]),
    new PermissionGroup('Payroll', [new PermissionOption('RunPayroll', 'Run Payroll')]),
    new PermissionGroup('Reports', [new PermissionOption('Reports', 'Create / Download Reports')]),
    new PermissionGroup(
      'Notifications',
      [
        new PermissionOption(
          'Notifications',
          'Receive Admin Notifications',
          (opts: SelectablePermission[]) => {
            if (!this.isCsrPortal) {
              //we aren't in the CSR portal. Fail now.
              return of(false);
            }
            return of(opts.some(opt => opt.val != 'Notifications' && opt.selected));
          }
        )
      ],
      (opts: SelectablePermission[]) => {
        if (!this.isCsrPortal) {
          //we aren't in the CSR portal. Fail now.
          return false;
        }
        return opts.some(opt => opt.val != 'Notifications' && opt.selected);
      }
    ),
    new PermissionGroup('Requests', [
      new PermissionOption('addressChange', 'Address Change'),
      new PermissionOption('directDepositChange', 'Direct Deposit Change'),
      new PermissionOption('timeOff', 'Time Off')
    ])
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private ngModalService: NgbModal,
    @Inject('env') private env: any,
    private appConfigService: AppConfigService,
    private emailBlacklistValidator: EmailDomainBlacklistValidator,
    private whitelistService: ClientFeatureWhitelistService,
    private store: Store,
    private swipeclockIdValidator: SwipeclockIdValidator,
    private companyService: CompaniesService
  ) {}

  get f() {
    return this.form.controls;
  }

  ngOnInit(): void {
    const admin = 'Client Portal Admin';
    this.swipeclockIdValidator.init(this.clientCode);
    let initialAccessLevel = false;
    if (this.initialModel != null) {
      if (this.initialModel.Roles.some(x => x.Name.includes(admin))) {
        initialAccessLevel = true;
      }
    }
    this.companyService.getSwipeclockMapping(this.clientCode).subscribe(x => {
      this.companySwipeclockIdSynched = !!x;
    });
    this.form = this.fb.group(
      {
        Name: new UntypedFormControl(
          {
            value: this.initialModel?.FirstName
              ? `${this.initialModel?.FirstName} ${this.initialModel?.LastName}`
              : '',
            disabled: this.initialModel != null
          },
          [Validators.required]
        ),
        IncludeTimekeeping: this.fb.control(this.initialModel?.TimekeepingId != null),
        TimekeepingId: this.fb.control(this.initialModel?.TimekeepingId, []),
        Email: new UntypedFormControl(
          { value: this.initialModel?.UserName ?? '', disabled: this.initialModel != null },
          [Validators.required, Validators.email],
          [this.emailBlacklistValidator.validate.bind(this.emailBlacklistValidator)]
        ),
        AccessLevel: new UntypedFormControl(initialAccessLevel),
        Permissions: new UntypedFormControl(this.initialModel?.Permissions ?? [])
      },
      { validators: [PermissionsValidator] }
    );
    this.form.get('IncludeTimekeeping').valueChanges.subscribe(val => {
      if (!val) {
        this.form.get('TimekeepingId').reset();
      }
    });
    this.form.get('Permissions').valueChanges.subscribe(val => {
      if (val.length == 0) {
        this.form.get('AccessLevel').enable();
      } else {
        this.form.get('AccessLevel').disable();
      }
    });

    this.modalRef = this.ngModalService.open(this.modalContent, {
      size: 'lg',
      windowClass: 'payline-detail-modal centered',
      backdrop: 'static',
      keyboard: false
    });

    this.modalRef.result.then(
      // close
      result => this._onSave(),
      // dismiss
      () => this._onCancel()
    );
  }

  public submit() {
    const email = this.form.get('Email');
    const timekeepingId = this.form.get('TimekeepingId');

    const validationResults = [this.emailBlacklistValidator.validate(email)];
    if (this.companySwipeclockIdSynched) {
      validationResults.push(this.swipeclockIdValidator.validate(timekeepingId));
    }

    forkJoin(validationResults).subscribe(res => {
      if (res[0] != null) {
        this.invalidDomain = `${res[0].blacklistedDomain} is not a valid domain. You cannot add this user.`;
      }
      if (res.length > 1 && res[1] != null) {
        this.invalidTimekeepingId = true;
      }
      if (!res[0] && (res.length == 1 || !res[1])) {
        this.modalRef.close();
      }
    });
  }

  private _onCancel() {
    this.onCloseDetails.emit(null);
  }
  private _onSave() {
    const result = this.form.value;
    result.AccessLevel = result.AccessLevel ? 'Client Portal Admin' : 'Client Portal User';
    this.onCloseDetails.emit(result);
  }
}

export const PermissionsValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const permissionControl = control.get('Permissions');
  const roleControl = control.get('AccessLevel');
  //If we aren't a Client Portal User, this should always be true.
  if (roleControl.value) return null;
  //If we have a permission, this should pass.
  if (permissionControl.value == null || permissionControl.value.length > 0) return null;
  return {
    missingPermissions: true
  };
};
